import {defineAbilityConfig} from '../system/defineAbilityConfig';

/**
 * Determines whether a user can request edit access.
 *
 * This ability is available to:
 * 1. Read-only users (anonymous comment users and unauthorized users)
 *    - These users don't have PermissionsStore, so they can't have design permission
 *    - Anonymous comment users: Accessing via comments domain (URL has `project` query param)
 *      - Can be in any workflow (comment, preview, canvas, etc.)
 *      - May or may not have filled out their email/identity
 *    - Unauthorized users: Accessing via shareable preview links (URL has `preview` or `p` query param)
 *      - Read-only preview mode users
 *
 * 2. Registered users with commenter site role in comment workflow
 *    - These users have PermissionsStore but don't have design permission
 *    - They can request access to edit the site
 *
 * Editors and designers already have edit access and cannot request it.
 */
export const canRequestAccess = defineAbilityConfig([
  {
    // Read-only users: anonymous comment users and unauthorized users
    // These users don't have PermissionsStore, so they can't have design permission
    userTypes: {
      anonymousComment: true,
    },
  },
  {
    // Registered users with commenter site role in comment workflow
    // Commenters don't have design abilities, so they can request edit access
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {
      comment: true,
      preview: true,
    },
    abilities: {
      canDesign: false,
      canAccessDesignSiteMode: false,
      canAccessEditSiteMode: false,
    },
  },
] as const);

/**
 * Determines whether a user can simulate site roles.
 *
 * This ability requires the user to have the `simulateRoles` permission on the site.
 */
export const canSimulateSiteRole = defineAbilityConfig({
  permissions: [
    ['simulateRoles', 'site'],
    ['design', 'site'],
  ],
} as const);
